import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiCheck } from "react-icons/fi";
import { IoMdCall } from "react-icons/io";
import Header from "../../components/layouts/Header";
import CircularProgress from "./componenets/CircularProgress";
import StepOneDetails from "./componenets/StepOneDetails";
import StepTwoPhotos from "./componenets/StepTwoPhotos";
import StepThreePricing from "./componenets/StepThreePricing";
import StepFourAmenities from "./componenets/StepFourAmenities";
import {
  setPostPropertyStepsStatus,
  setpropertyScooring,
} from "../../redux/property/PropertySlice";
import "./PostPropertyNew.css";

const PostPropertyHome = () => {
  const dispatch = useDispatch();
  const { postPropertyStepsStatus, propertyScooring } = useSelector(
    (state) => state.Property
  );

  useEffect(() => {
    dispatch(setpropertyScooring());
  }, [dispatch]);

  // Map internal status to active step index:
  // 0, 1, 2 -> Step 1 (Basic Details & Profile)
  // 3 -> Step 2 (Photos)
  // 4 -> Step 3 (Pricing & Others)
  // 5 -> Step 4 (Amenities & Review)
  const getActiveStep = () => {
    if (postPropertyStepsStatus <= 2) return 1;
    if (postPropertyStepsStatus === 3) return 2;
    if (postPropertyStepsStatus === 4) return 3;
    if (postPropertyStepsStatus >= 5) return 4;
    return 1;
  };

  const activeStep = getActiveStep();

  const handleStepClick = (stepIndex) => {
    if (stepIndex === 1) {
      dispatch(setPostPropertyStepsStatus(0));
    } else if (stepIndex === 2 && activeStep >= 2) {
      dispatch(setPostPropertyStepsStatus(3));
    } else if (stepIndex === 3 && activeStep >= 3) {
      dispatch(setPostPropertyStepsStatus(4));
    } else if (stepIndex === 4 && activeStep >= 4) {
      dispatch(setPostPropertyStepsStatus(5));
    }
  };

  const stepsList = [
    { id: 1, label: "Basic Details" },
    { id: 2, label: "Photos" },
    { id: 3, label: "Pricing & Others" },
    { id: 4, label: "Amenities & Review" },
  ];

  // Dynamic score computation for visual display matching screenshots
  const displayScore = Math.max(
    propertyScooring || 0,
    activeStep === 1
      ? 39
      : activeStep === 2
        ? 57
        : activeStep === 3
          ? 59
          : 64
  );

  return (
    <div className="mgh-post-property-page">
      {/* Official Header with logo4.png, mega menu, and orange button */}
      <Header />

      <div className="mgh-post-container">
        {/* Top Horizontal Stepper (Matching User Reference Screenshots) */}
        <div className="mgh-post-stepper-wrap">
          <div className="mgh-post-stepper">
            {/* Stepper background track line */}
            <div className="mgh-stepper-line">
              <div
                className="mgh-stepper-line-progress"
                style={{
                  width: `${((activeStep - 1) / (stepsList.length - 1)) * 100}%`,
                }}
              />
            </div>

            {/* Stepper Items */}
            {stepsList.map((step) => {
              const isCompleted = step.id < activeStep;
              const isActive = step.id === activeStep;
              const isPending = step.id > activeStep;

              return (
                <button
                  type="button"
                  key={step.id}
                  className={`mgh-stepper-item ${isCompleted
                      ? "completed"
                      : isActive
                        ? "active"
                        : "pending"
                    }`}
                  onClick={() => handleStepClick(step.id)}
                  disabled={isPending}
                >
                  <div className="mgh-stepper-circle">
                    {isCompleted ? <FiCheck /> : step.id}
                  </div>
                  <span className="mgh-stepper-label">{step.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main 2-Column Grid */}
        <div className="mgh-post-grid">
          {/* Left Column: Form Content */}
          <div className="mgh-post-form-col">
            {activeStep === 1 && (
              <StepOneDetails
                onNext={() => dispatch(setPostPropertyStepsStatus(3))}
              />
            )}
            {activeStep === 2 && (
              <StepTwoPhotos
                onBack={() => dispatch(setPostPropertyStepsStatus(0))}
                onNext={() => dispatch(setPostPropertyStepsStatus(4))}
              />
            )}
            {activeStep === 3 && (
              <StepThreePricing
                onBack={() => dispatch(setPostPropertyStepsStatus(3))}
                onNext={() => dispatch(setPostPropertyStepsStatus(5))}
              />
            )}
            {activeStep === 4 && (
              <StepFourAmenities
                onBack={() => dispatch(setPostPropertyStepsStatus(4))}
              />
            )}
          </div>

          {/* Right Column: Sticky Info Sidebar */}
          <div className="mgh-post-sidebar-col">
            {/* Card 1: Property Score Circular Meter */}
            <div className="mgh-sidebar-card mgh-score-card">
              <div className="mgh-score-circle-wrap">
                <CircularProgress percentage={displayScore} />
              </div>
              <div className="mgh-score-info">
                <h4>Property Score</h4>
                <p>Better your property score, greater your visibility</p>
              </div>
            </div>

            {/* Card 2: Guidance Illustration Card */}
            <div className="mgh-sidebar-card mgh-tip-card">
              <img
                src="/assets/images/all/Amenities.png"
                alt="Feature guidance"
                className="mgh-tip-img"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <p className="mgh-tip-text">
                These are the features that buyers look for. Highlighting them
                attracts more responses.
              </p>
            </div>

            {/* Card 3: Need Help Box */}
            <div className="mgh-sidebar-card mgh-help-card">
              <div className="mgh-help-header">
                <div className="mgh-help-icon">
                  <IoMdCall />
                </div>
                <h4>Need help ?</h4>
              </div>
              <div className="mgh-help-body">
                You can email us at{" "}
                <a href="mailto:service@ewrealty.com">
                  service@ewrealty.com
                </a>{" "}
                or call us at{" "}
                <a href="tel:18004199099">1800 41 99099</a> (IND Toll-Free).
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostPropertyHome;
